import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useDebounced } from '@/lib/catalogue';
import { useClientOrders, useCreerCommande, useCustomers, type ClientOrder } from '@/lib/orders';
import { formatMoney } from '@/lib/utils';

/**
 * Historique des commandes d'un client — pour la consultation « super rep » : voir
 * ce qu'un client a déjà commandé (tous reps confondus), avec le détail des
 * articles dépliable. Consultation seule.
 */
export default function ClientOrders({
  customerId: controlledId,
}: { customerId?: number | null } = {}) {
  // Mode « contrôlé » : le client vient du parent (consultation à recherche unique).
  const controlled = controlledId !== undefined;
  const [q, setQ] = useState('');
  const debounced = useDebounced(q, 250);
  const recherche = debounced.trim() !== '';
  const clients = useCustomers(debounced, recherche);
  const [ownCustomer, setOwnCustomer] = useState<{
    id: number;
    name: string;
    custNum: string;
  } | null>(null);
  const customerId = controlled ? (controlledId ?? null) : (ownCustomer?.id ?? null);
  const orders = useClientOrders(customerId);
  const navigate = useNavigate();
  const creer = useCreerCommande();

  if (!controlled && customerId === null) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold">Commandes d'un client</h2>
          <p className="text-muted-foreground text-sm">
            Cherchez un client pour voir tout ce qu'il a déjà commandé.
          </p>
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Nom, n° de client ou ville…"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
          className="border-input bg-background h-12 w-full max-w-lg rounded-lg border px-3 text-base"
        />

        {recherche && (
          <div className="grid max-w-lg gap-2">
            {clients.data?.customers.map((c) => (
              <button
                key={c.id}
                onClick={() => setOwnCustomer({ id: c.id, name: c.name, custNum: c.custNum })}
                className="bg-card hover:border-primary/50 flex items-center gap-3 rounded-lg border p-3 text-left"
              >
                <span className="bg-secondary text-secondary-foreground rounded px-2 py-0.5 font-mono text-xs font-medium">
                  {c.custNum}
                </span>
                <span className="font-medium">{c.name}</span>
              </button>
            ))}
            {!clients.isPending && clients.data?.customers.length === 0 && (
              <p className="text-muted-foreground text-sm">Aucun client trouvé.</p>
            )}
          </div>
        )}
      </div>
    );
  }

  const data = orders.data;
  const totalCents = (data?.orders ?? []).reduce((a, o) => a + Math.round(Number(o.total) * 100), 0);
  const totalAchats = (totalCents / 100).toFixed(2);

  function nouvelleCommande() {
    if (creer.isPending || customerId === null) return;
    creer.mutate(customerId, {
      onSuccess: (d) => navigate(`/order/${d.order.id}`),
    });
  }

  const boutonNouvelle = (
    <button
      onClick={nouvelleCommande}
      disabled={creer.isPending}
      className="bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
    >
      {creer.isPending ? 'Création…' : '+ Nouvelle commande'}
    </button>
  );

  return (
    <div className="space-y-5">
      {/* En mode contrôlé, l'entête client (nom + changer de client) est géré par le
          parent ; on ne garde ici que l'action « + Nouvelle commande ». */}
      {controlled ? (
        <div className="flex justify-end">{boutonNouvelle}</div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold">{ownCustomer?.name}</h2>
            <p className="text-muted-foreground font-mono text-sm">{ownCustomer?.custNum}</p>
          </div>
          <div className="flex gap-2">
            {boutonNouvelle}
            <button
              onClick={() => {
                setOwnCustomer(null);
                setQ('');
              }}
              className="border-input rounded-lg border px-4 py-2 text-sm font-medium"
            >
              ← Autre client
            </button>
          </div>
        </div>
      )}

      {/* Total de tous les achats (commandes soumises) du client. */}
      <div className="bg-card flex items-center justify-between rounded-xl border p-4">
        <span className="text-muted-foreground text-sm font-medium">Total des achats du client</span>
        <span className="text-2xl font-bold tabular-nums">
          {orders.isPending ? '…' : formatMoney(totalAchats)}
        </span>
      </div>

      {creer.isError && (
        <p className="text-destructive text-sm font-medium">{(creer.error as Error).message}</p>
      )}

      {orders.isPending ? (
        <p className="text-muted-foreground text-sm">Chargement…</p>
      ) : !data || data.orders.length === 0 ? (
        <div className="rounded-xl border border-dashed p-10 text-center">
          <p className="font-medium">Aucune commande</p>
          <p className="text-muted-foreground mt-1 text-sm">
            Ce client n'a aucune commande soumise pour l'instant.
          </p>
        </div>
      ) : (
        <>
          <p className="text-muted-foreground text-sm">
            {data.orders.length} commande{data.orders.length > 1 ? 's' : ''} soumise
            {data.orders.length > 1 ? 's' : ''}
          </p>
          {data.orders.map((o) => (
            <OrderCard key={o.id} order={o} />
          ))}
        </>
      )}
    </div>
  );
}

function OrderCard({ order }: { order: ClientOrder }) {
  const [ouvert, setOuvert] = useState(false);
  return (
    <section className="bg-card rounded-xl border">
      <button
        onClick={() => setOuvert((v) => !v)}
        className="flex w-full items-center justify-between gap-3 p-4 text-left"
      >
        <div className="min-w-0">
          <p className="font-semibold">
            {order.number ?? '—'}{' '}
            <span className="text-muted-foreground text-sm font-normal">
              · {order.lines.length} article{order.lines.length > 1 ? 's' : ''}
            </span>
          </p>
          <p className="text-muted-foreground text-xs">
            {order.submittedAt ? new Date(order.submittedAt).toLocaleString('fr-CA') : '—'} · par{' '}
            {order.repName}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span className="text-lg font-bold tabular-nums">{formatMoney(order.total)}</span>
          <span className="text-muted-foreground">{ouvert ? '▲' : '▼'}</span>
        </div>
      </button>

      {ouvert && (
        <div className="overflow-x-auto border-t">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground text-left">
                <th className="p-3 font-medium">Code</th>
                <th className="p-3 font-medium">Description</th>
                <th className="p-3 text-right font-medium">Qté</th>
                <th className="p-3 text-right font-medium">Prix</th>
                <th className="p-3 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {order.lines.map((l, i) => (
                <tr key={i} className="border-t">
                  <td className="p-3 font-mono text-xs whitespace-nowrap">
                    {l.lineCode}-{l.partNumber}
                  </td>
                  <td className="p-3">{l.description}</td>
                  <td className="p-3 text-right tabular-nums">{l.qty}</td>
                  <td className="p-3 text-right tabular-nums">{formatMoney(l.unitPrice)}</td>
                  <td className="p-3 text-right font-medium tabular-nums">
                    {formatMoney(l.lineTotal)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
